import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Tooltip,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
    TextField,
    Chip,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { DeleteRounded, Edit } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { processAssignmentService } from '../../../services/admin/processAssignmentService'
import { adminProcessService } from '../../../services/admin/process'
import { userConfig } from '../../../services/userConfigServices'
import { activityTemplateService } from '../../../services/activityTemplateService'
import ConfirmationDialog from '../Workflow/components/ConfirmationDialog'
import { GroupHeader, GroupItems } from '../components/CreateProcessTemplateDialog'
import { formatUserFullName } from '../../../utility/userName'

const ProcessAssignmentConfig = () => {
    const { user } = useAuthContext()
    const [loading, setLoading] = useState(false)
    const [assignments, setAssignments] = useState([])
    const [processes, setProcesses] = useState([])
    const [users, setUsers] = useState([])
    const [activities, setActivities] = useState([])
    const [showDialog, setShowDialog] = useState(false)
    const [editingAssignment, setEditingAssignment] = useState(null)
    const [deletingAssignment, setDeletingAssignment] = useState(null)

    const [formData, setFormData] = useState({
        process_template_id: null,
        assignment_type: 'static', // 'static' or 'dynamic'
        user_id: null,
        assignee_from_activity_template_id: null,
    })

    useEffect(() => {
        if (!user) return
        loadData()
    }, [user])

    const loadData = async () => {
        setLoading(true)
        try {
            const [assignmentsData, processesData, usersData, activitiesData] = await Promise.all([
                processAssignmentService.getProcessAssignments(user),
                adminProcessService.getProcesses(user),
                userConfig.getUsersByCompany(user),
                activityTemplateService.getAllActivitiesOfCompany(user, user.company_id),
            ])

            setAssignments(assignmentsData)
            setProcesses(processesData)
            setUsers(usersData.data)

            // Enrich activities with process names for grouping
            const enrichedActivities = activitiesData.map(activity => {
                const process = processesData.find(p => p.id === activity.process_template_id)
                return {
                    ...activity,
                    id: activity.activity_template_id, // Add id field for consistency
                    process_template_name: process?.process_template_name || 'Unknown Process'
                }
            })
            setActivities(enrichedActivities)
        } catch (error) {
            console.error('Error loading data:', error)
            toast.error('Failed to load configuration data')
        } finally {
            setLoading(false)
        }
    }

    const openCreateDialog = () => {
        setEditingAssignment(null)
        setFormData({
            process_template_id: null,
            assignment_type: 'static',
            user_id: null,
            assignee_from_activity_template_id: null,
        })
        setShowDialog(true)
    }

    const openEditDialog = (assignment) => {
        setEditingAssignment(assignment)
        setFormData({
            process_template_id: assignment.process_template_id,
            assignment_type: assignment.user_id ? 'static' : 'dynamic',
            user_id: assignment.user_id,
            assignee_from_activity_template_id: assignment.assignee_from_activity_template_id,
        })
        setShowDialog(true)
    }

    const handleSave = async () => {
        try {
            if (!formData.process_template_id) {
                toast.error('Please select a process template')
                return
            }

            if (formData.assignment_type === 'static' && !formData.user_id) {
                toast.error('Please select a user')
                return
            }

            if (formData.assignment_type === 'dynamic' && !formData.assignee_from_activity_template_id) {
                toast.error('Please select an activity')
                return
            }

            const payload = {
                process_template_id: formData.process_template_id,
                user_id: formData.assignment_type === 'static' ? formData.user_id : null,
                assignee_from_activity_template_id:
                    formData.assignment_type === 'dynamic'
                        ? formData.assignee_from_activity_template_id
                        : null,
            }

            if (editingAssignment) {
                await processAssignmentService.updateProcessAssignment(user, editingAssignment.id, payload)
                toast.success('Assignment updated successfully')
            } else {
                await processAssignmentService.createProcessAssignment(user, payload)
                toast.success('Assignment created successfully')
            }

            setShowDialog(false)
            loadData()
        } catch (error) {
            console.error('Error saving assignment:', error)
            toast.error(error.response?.data?.error || 'Failed to save assignment')
        }
    }

    const handleDelete = async (id) => {
        try {
            await processAssignmentService.deleteProcessAssignment(user, id)
            toast.success('Assignment deleted successfully')
            setDeletingAssignment(null)
            loadData()
        } catch (error) {
            console.error('Error deleting assignment:', error)
            toast.error('Failed to delete assignment')
        }
    }

    const getProcessActivities = () => {
        if (!formData.process_template_id) return []
        // For dynamic assignment, show activities from OTHER processes (not the one being initiated)
        return activities.filter((act) => act.process_template_id !== formData.process_template_id)
    }

    return (
        <Box>
            <Box style={{ marginBottom: 16 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Process Assignment Configuration
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Configure user assignments for auto-initiated processes
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} style={{ backgroundColor: 'white' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Process Template</TableCell>
                                    <TableCell>Assignment Type</TableCell>
                                    <TableCell>Assigned To</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignments.map((assignment) => (
                                    <TableRow key={assignment.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {assignment.process_template_name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {assignment.prefix}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={assignment.user_id ? 'Static User' : 'Dynamic Activity'}
                                                size="small"
                                                color={assignment.user_id ? 'primary' : 'secondary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {assignment.user_id ? (
                                                <Typography variant="body2">{assignment.user_name}</Typography>
                                            ) : (
                                                <Typography variant="body2" fontStyle="italic">
                                                    From: {assignment.assignee_activity_name || 'N/A'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEditDialog(assignment)}>
                                                    <Edit style={{ fontSize: 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeletingAssignment(assignment)}
                                                >
                                                    <DeleteRounded style={{ fontSize: 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {assignments.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                No process assignments configured yet
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Button
                        variant="contained"
                        color="primary"
                        style={{ marginTop: 16 }}
                        onClick={openCreateDialog}
                    >
                        Add Process Assignment
                    </Button>

                    {/* Create/Edit Dialog */}
                    <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>
                            {editingAssignment ? 'Edit Process Assignment' : 'Create Process Assignment'}
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                <Autocomplete
                                    options={processes}
                                    getOptionLabel={(option) => `${option.process_template_name} (${option.prefix})`}
                                    value={processes.find((p) => p.id === formData.process_template_id) || null}
                                    onChange={(_, value) =>
                                        setFormData({ ...formData, process_template_id: value?.id || null })
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Process Template" required />
                                    )}
                                    disabled={!!editingAssignment}
                                    groupBy={(option) => option.company_id}
                                    renderGroup={(params) => (
                                        <li key={params.key}>
                                            <GroupHeader>{params.group}</GroupHeader>
                                            <GroupItems>{params.children}</GroupItems>
                                        </li>
                                    )}
                                />

                                <FormControl component="fieldset">
                                    <FormLabel>Assignment Type</FormLabel>
                                    <RadioGroup
                                        value={formData.assignment_type}
                                        onChange={(e) =>
                                            setFormData({ ...formData, assignment_type: e.target.value })
                                        }
                                    >
                                        <FormControlLabel
                                            value="static"
                                            control={<Radio />}
                                            label="Assign to Specific User"
                                        />
                                        <FormControlLabel
                                            value="dynamic"
                                            control={<Radio />}
                                            label="Assign from Previous Process Activity"
                                        />
                                    </RadioGroup>
                                </FormControl>

                                {formData.assignment_type === 'static' && (
                                    <Autocomplete
                                        options={users}
                                        getOptionLabel={(option) => formatUserFullName(option)}
                                        value={users.find((u) => u.id === formData.user_id) || null}
                                        onChange={(_, value) => setFormData({ ...formData, user_id: value?.id || null })}
                                        renderInput={(params) => <TextField {...params} label="User" required />}
                                    />
                                )}

                                {formData.assignment_type === 'dynamic' && (
                                    <Autocomplete
                                        options={getProcessActivities()}
                                        getOptionLabel={(option) => option.activity_template_name}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        value={
                                            getProcessActivities().find(
                                                (a) => a.id === formData.assignee_from_activity_template_id
                                            ) || null
                                        }
                                        onChange={(_, value) => {
                                            console.log('Activity selected:', value)
                                            setFormData({
                                                ...formData,
                                                assignee_from_activity_template_id: value?.id || null,
                                            })
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Activity from Previous Process" required />
                                        )}
                                        disabled={!formData.process_template_id}
                                        groupBy={(option) => option.process_template_name}
                                        renderGroup={(params) => (
                                            <li key={params.key}>
                                                <GroupHeader>{params.group}</GroupHeader>
                                                <GroupItems>{params.children}</GroupItems>
                                            </li>
                                        )}
                                    />
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setShowDialog(false)}>Cancel</Button>
                            <Button onClick={handleSave} variant="contained" color="primary">
                                Save
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Delete Confirmation */}
                    <ConfirmationDialog
                        title="Delete Assignment?"
                        message={`Are you sure you want to delete the assignment for "${deletingAssignment?.process_template_name}"?`}
                        open={deletingAssignment != null}
                        onClose={() => setDeletingAssignment(null)}
                        onConfirm={() => handleDelete(deletingAssignment?.id)}
                    />
                </>
            )}
        </Box>
    )
}

export default ProcessAssignmentConfig
